import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Phone, Mail, CheckCircle2, Loader2 } from 'lucide-react';
import axios from 'axios';
import { contactInfo, maxBotLink, maxBotQr } from '../../data/mockData';

// Центроид трёх домов Любимово 7/11/18
const MAP_CENTER = '39.0395,45.0817';
const MAP_ALL = `https://yandex.ru/map-widget/v1/?pt=${contactInfo.addresses[0].lon},${contactInfo.addresses[0].lat},pm2rdm~${contactInfo.addresses[1].lon},${contactInfo.addresses[1].lat},pm2blm~${contactInfo.addresses[2].lon},${contactInfo.addresses[2].lat},pm2gnm&z=15&l=map&ll=${MAP_CENTER}`;


const API_BASE = process.env.REACT_APP_BACKEND_URL ?? '';

const BrutalistContact = () => {
  const [selected, setSelected] = useState(null);

  // Form state
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const mapSrc = selected !== null
    ? `https://yandex.ru/map-widget/v1/?pt=${contactInfo.addresses[selected].lon},${contactInfo.addresses[selected].lat},pm2rdm&z=18&l=map&ll=${contactInfo.addresses[selected].lon},${contactInfo.addresses[selected].lat}`
    : MAP_ALL;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const n = name.trim();
    const p = phone.trim();
    if (n.length < 2) { setError('Введите имя'); return; }
    if (p.replace(/\D/g, '').length < 10) { setError('Введите телефон полностью'); return; }
    if (!consent) { setError('Подтвердите согласие на обработку персональных данных'); return; }

    setBusy(true);
    const payload = {
      name: n,
      phone: p,
      message: 'Заявка с главной страницы (секция «Связь»)',
      source: 'главная-связь',
      consent: true,
    };

    // Каскад как в CallbackForm: axios → fetch → native form через iframe
    let ok = false; let lastErr = null;
    const url = `${API_BASE}/api/messages`;

    try { await axios.post(url, payload); ok = true; }
    catch (err) { lastErr = err; }

    if (!ok) {
      try {
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          mode: 'cors', credentials: 'omit', cache: 'no-store', keepalive: true,
        });
        if (res.ok) ok = true; else lastErr = new Error(`HTTP ${res.status}`);
      } catch (err) { lastErr = err; }
    }

    if (!ok) {
      // native form через скрытый iframe (обходит AdBlock/MetaMask SES)
      try {
        await new Promise((resolve, reject) => {
          const traceId = 'home_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
          const iframeName = 'home_iframe_' + traceId;
          const iframe = document.createElement('iframe');
          iframe.name = iframeName; iframe.style.display = 'none';
          document.body.appendChild(iframe);
          const form = document.createElement('form');
          form.method = 'POST'; form.action = `${API_BASE}/api/messages-form`;
          form.target = iframeName; form.enctype = 'application/x-www-form-urlencoded';
          form.acceptCharset = 'UTF-8';
          Object.entries({ ...payload, consent: 'true', trace_id: traceId }).forEach(([k, v]) => {
            const i = document.createElement('input');
            i.type = 'hidden'; i.name = k; i.value = String(v);
            form.appendChild(i);
          });
          document.body.appendChild(form);
          const cleanup = () => {
            form.parentNode && document.body.removeChild(form);
            iframe.parentNode && document.body.removeChild(iframe);
          };
          const timer = setTimeout(() => { cleanup(); reject(new Error('timeout')); }, 12000);
          let loadCount = 0;
          iframe.onload = () => { if (++loadCount < 2) return; clearTimeout(timer); cleanup(); resolve(true); };
          form.submit();
        });
        ok = true;
      } catch (err) { lastErr = err; }
    }

    setBusy(false);
    if (ok) {
      setShowSuccess(true);
      setName(''); setPhone(''); setConsent(false);
    } else {
      let detail = '';
      if (lastErr?.code === 'ERR_NETWORK') detail = ' (нет связи — отключите блокировщик)';
      else if (lastErr?.message) detail = ` (${lastErr.message})`;
      setError(`Не удалось отправить${detail}. Позвоните: +7 918 436-22-55`);
    }
  };

  return (
    <section className="py-24 px-6 bg-background text-foreground">
      <div className="max-w-5xl mx-auto">

        <h2 className="text-4xl md:text-5xl font-sans font-bold uppercase mb-12 tracking-tighter">
          Связь
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-16">

          {/* Left — contacts */}
          <div className="space-y-8 font-mono">

            {/* Phone + QR */}
            <div>
              <div className="text-xs text-muted-foreground uppercase mb-1">Телефон</div>
              <a href={`tel:${contactInfo.phoneRaw}`} className="text-xl font-bold hover:text-accent transition-colors flex items-center gap-2">
                <Phone className="w-4 h-4" />
                {contactInfo.phone}
              </a>
            </div>

            {/* Max messenger */}
            <div>
              <div className="text-xs text-muted-foreground uppercase mb-2">Написать в Max</div>
              <a
                href={maxBotLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#005FF9] text-white text-sm font-mono uppercase tracking-wider hover:bg-[#0050d4] transition-colors"
              >
                Написать в Max
              </a>
              <div className="mt-3">
                <div className="text-xs text-muted-foreground uppercase mb-2">QR-код Max</div>
                <img
                  src={maxBotQr}
                  alt="QR Max"
                  className="border border-black"
                  width={120}
                  height={120}
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <div className="text-xs text-muted-foreground uppercase mb-1">Email для бронирования</div>
              <a href={`mailto:${contactInfo.email}`} className="text-sm sm:text-base hover:text-accent transition-colors flex items-center gap-2 break-all">
                <Mail className="w-4 h-4 shrink-0" />
                {contactInfo.email}
              </a>
            </div>

            {/* Addresses */}
            <div>
              <div className="text-xs text-muted-foreground uppercase mb-3">Адреса складов</div>
              <div className="space-y-2">
                {contactInfo.addresses.map((addr, i) => (
                  <button
                    key={i}
                    onClick={() => setSelected(selected === i ? null : i)}
                    className={`w-full text-left flex items-start gap-3 p-3 border transition-all duration-200 ${
                      selected === i
                        ? 'border-accent bg-accent/5 text-accent'
                        : 'border-black/20 hover:border-accent hover:bg-accent/5'
                    }`}
                  >
                    <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                    <div>
                      <div className="text-xs uppercase tracking-wider opacity-60 mb-0.5">{addr.label}</div>
                      <div className="text-base">{addr.short}</div>
                    </div>
                  </button>
                ))}
              </div>
              {selected !== null && (
                <p className="mt-2 text-xs text-muted-foreground font-mono">
                  ↓ карта приближена к {contactInfo.addresses[selected].label}
                </p>
              )}
            </div>
          </div>

          {/* Right — map + form */}
          <div className="space-y-8">
            {/* Map */}
            <div className="h-72 w-full border border-black overflow-hidden grayscale hover:grayscale-0 transition-all duration-500">
              <iframe
                key={mapSrc}
                src={mapSrc}
                width="100%"
                height="100%"
                frameBorder="0"
                title="Кладоффка24 на карте"
                style={{ border: 0 }}
              />
            </div>

            {/* Form */}
            <form className="space-y-6" onSubmit={onSubmit}>
              <div className="group">
                <label className="block font-mono text-xs uppercase mb-2 text-muted-foreground group-focus-within:text-accent transition-colors">
                  Имя
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-transparent border-b border-black py-2 focus:outline-none focus:border-accent transition-colors font-sans text-xl rounded-none"
                  placeholder="Иван Иванов"
                  required
                />
              </div>
              <div className="group">
                <label className="block font-mono text-xs uppercase mb-2 text-muted-foreground group-focus-within:text-accent transition-colors">
                  Телефон
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full bg-transparent border-b border-black py-2 focus:outline-none focus:border-accent transition-colors font-sans text-xl rounded-none"
                  placeholder="+7 (___) ___-__-__"
                  required
                />
              </div>

              <label className="flex items-start gap-2 cursor-pointer pt-2">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 w-4 h-4 border border-black accent-black cursor-pointer flex-shrink-0"
                />
                <span className="font-mono text-[11px] text-black/70 leading-relaxed">
                  Принимаю <a href="/oferta" target="_blank" rel="noopener noreferrer" className="underline font-bold">Договор-оферту</a>, <a href="/rules" target="_blank" rel="noopener noreferrer" className="underline font-bold">Правила</a> и согласен с <a href="/privacy" target="_blank" className="underline">политикой конфиденциальности</a>
                </span>
              </label>

              {error && (
                <p className="font-mono text-xs text-red-600 uppercase">// {error}</p>
              )}

              <button
                type="submit"
                disabled={busy || !consent}
                className="w-full bg-accent text-accent-foreground py-4 font-mono uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-accent/90 transition-all hover:translate-y-[-2px] active:translate-y-[0px] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                {busy ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    ОТПРАВКА...
                  </>
                ) : (
                  <>Отправить заявку</>
                )}
              </button>
            </form>

            {/* Success modal */}
            <AnimatePresence>
              {showSuccess && (
                <motion.div
                  className="fixed inset-0 z-[100] flex items-center justify-center px-4 bg-black/70 backdrop-blur-sm"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  onClick={() => setShowSuccess(false)}
                >
                  <motion.div
                    initial={{ scale: 0.85, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.85, y: 30 }}
                    transition={{ type: 'spring', stiffness: 260, damping: 22 }}
                    className="bg-[#F5F3EE] border-2 border-black rounded-none p-10 max-w-md w-full text-center shadow-[8px_8px_0_0_#000]"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="w-16 h-16 border-2 border-black flex items-center justify-center mx-auto mb-6 bg-[#E63B2E]">
                      <CheckCircle2 className="w-8 h-8 text-white" />
                    </div>
                    <h4 className="text-2xl font-bold font-sans uppercase mb-3">Заявка отправлена!</h4>
                    <p className="font-mono text-sm text-black/70 leading-relaxed mb-6">
                      Оператор перезвонит в течение 15 минут.
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowSuccess(false)}
                      className="w-full py-3 bg-black text-white font-mono uppercase tracking-widest text-sm border-2 border-black hover:bg-[#E63B2E] transition-colors"
                    >
                      Понятно
                    </button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

        </div>
      </div>
    </section>
  );
};

export default BrutalistContact;
